import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Pension Calculator — three sub-scenarios: lump-sum vs monthly, work longer, single-life vs joint
export default function PensionCalc() {
  return (
    <CalcShell look="cnet" title="Pension Calculator" subtitle="Compare lump-sum payout vs monthly pension, evaluate working longer, and analyse single-life vs joint-and-survivor payouts."
      about={<><p>A <b>pension</b> pays retirees a periodic income for life (defined-benefit plan). Many plans allow retirees to choose between a monthly income stream and a lump-sum payout. This tool helps make that comparison.</p></>}
    >
      <LumpVsMonthly/>
      <hr className="my-8 border-slate-200"/>
      <WorkLonger/>
      <hr className="my-8 border-slate-200"/>
      <SingleVsJoint/>
    </CalcShell>
  );
}

function LumpVsMonthly() {
  const [retAge, setRetAge] = useState(65);
  const [lifeExp, setLifeExp] = useState(85);
  const [lumpSum, setLumpSum] = useState(800000);
  const [invReturn, setInvReturn] = useState(5);
  const [monthly, setMonthly] = useState(5000);
  const [cola, setCola] = useState(3.5);
  const c = useMemo(() => {
    const years = lifeExp - retAge;
    // PV of monthly pension
    let pv = 0; let curMon = monthly;
    for (let y = 1; y <= years; y++) {
      pv += (curMon * 12) / Math.pow(1 + invReturn/100, y);
      curMon *= (1 + cola/100);
    }
    // Future value of lump sum
    let lumpFV = lumpSum * Math.pow(1 + invReturn/100, years);
    return { pv, lumpFV, diff: lumpSum - pv, better: lumpSum > pv ? 'Lump sum' : 'Monthly income' };
  }, [retAge, lifeExp, lumpSum, invReturn, monthly, cola]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Lump sum payout or monthly pension income?</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your retirement age</label><input type="number" value={retAge} onChange={e=>setRetAge(+e.target.value||0)}/></div>
          <div className="field"><label>Your life expectancy</label><input type="number" value={lifeExp} onChange={e=>setLifeExp(+e.target.value||0)}/></div>
          <div className="field"><label>Lump sum payment amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={lumpSum} onChange={e=>setLumpSum(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Your investment return</label><div className="flex gap-2"><input type="number" step="0.01" value={invReturn} onChange={e=>setInvReturn(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
          <div className="field"><label>Monthly pension income</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthly} onChange={e=>setMonthly(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">per month</span></div></div>
          <div className="field"><label>Cost-of-living adjustment¹</label><div className="flex gap-2"><input type="number" step="0.01" value={cola} onChange={e=>setCola(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Better choice</div><div className="result-value" data-testid="pension-choice">{c.better}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Option 1: Lump sum today</td><td className="text-right">{fmt(lumpSum)}</td></tr>
            <tr><td>Option 2: PV of monthly pension</td><td className="text-right">{fmt(c.pv)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Difference</td><td className={`text-right ${c.diff>0?'text-green-600':'text-red-600'}`}>{fmt(Math.abs(c.diff))}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}

function WorkLonger() {
  const [pensionNow, setPensionNow] = useState(3000);
  const [pensionLater, setPensionLater] = useState(4500);
  const [retNow, setRetNow] = useState(62);
  const [retLater, setRetLater] = useState(67);
  const [lifeExp, setLifeExp] = useState(85);
  const [invReturn, setInvReturn] = useState(5);
  const [cola, setCola] = useState(3);
  const c = useMemo(() => {
    const totalNow = pensionNow * 12 * (lifeExp - retNow);
    const totalLater = pensionLater * 12 * (lifeExp - retLater);
    // Simplify: compute cumulative income streams over lifetime
    return { totalNow, totalLater, diff: totalLater - totalNow };
  }, [pensionNow, pensionLater, retNow, retLater, lifeExp]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Should you work longer for a better pension?</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Retire now</h4>
              <div className="field"><label className="text-xs">Retirement age</label><input type="number" value={retNow} onChange={e=>setRetNow(+e.target.value||0)}/></div>
              <div className="field"><label className="text-xs">Monthly pension</label><div className="flex gap-1"><span className="self-center">$</span><input type="number" value={pensionNow} onChange={e=>setPensionNow(+e.target.value||0)} className="flex-1"/></div></div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Work longer</h4>
              <div className="field"><label className="text-xs">Retirement age</label><input type="number" value={retLater} onChange={e=>setRetLater(+e.target.value||0)}/></div>
              <div className="field"><label className="text-xs">Monthly pension</label><div className="flex gap-1"><span className="self-center">$</span><input type="number" value={pensionLater} onChange={e=>setPensionLater(+e.target.value||0)} className="flex-1"/></div></div>
            </div>
          </div>
          <div className="field mt-3"><label>Life expectancy</label><input type="number" value={lifeExp} onChange={e=>setLifeExp(+e.target.value||0)}/></div>
          <div className="field"><label>Investment return</label><div className="flex gap-2"><input type="number" step="0.01" value={invReturn} onChange={e=>setInvReturn(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Cost-of-living adjustment</label><div className="flex gap-2"><input type="number" step="0.01" value={cola} onChange={e=>setCola(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <table className="data-table"><tbody>
            <tr><td>Total lifetime income (retire now)</td><td className="text-right">{fmt(c.totalNow)}</td></tr>
            <tr><td>Total lifetime income (work longer)</td><td className="text-right">{fmt(c.totalLater)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Difference</td><td className={`text-right ${c.diff>0?'text-green-600':'text-red-600'}`}>{fmt(Math.abs(c.diff))}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}

function SingleVsJoint() {
  const [retireAge, setRetireAge] = useState(65);
  const [yourLife, setYourLife] = useState(77);
  const [spouseAgeAtRet, setSpouseAgeAtRet] = useState(62);
  const [spouseLife, setSpouseLife] = useState(82);
  const [singleAmt, setSingleAmt] = useState(5000);
  const [jointAmt, setJointAmt] = useState(3000);
  const [returnRate, setReturnRate] = useState(5);
  const [cola, setCola] = useState(3.5);
  const c = useMemo(() => {
    const rMon = returnRate / 100 / 12;
    const colaMon = cola / 100 / 12;
    const yearsYou = Math.max(0, yourLife - retireAge);
    const monthsYou = yearsYou * 12;
    // Spouse's life beyond your death: spouse lives (spouseLife - spouseAgeAtRet) - yearsYou years
    const yearsSpouseSurvives = Math.max(0, (spouseLife - spouseAgeAtRet) - yearsYou);
    const monthsSpouseSurvives = yearsSpouseSurvives * 12;

    // FV of investing the pension-difference $ each month for monthsYou (single-life scenario)
    const diff = Math.max(0, singleAmt - jointAmt);
    let fvDiff = 0;
    let curDiff = diff;
    for (let m = 0; m < monthsYou; m++) {
      fvDiff = fvDiff * (1 + rMon) + curDiff;
      curDiff = curDiff * (1 + colaMon);
    }
    // Value of remaining survivor-pension benefit (joint case) at time of your death
    let survivorPV = 0;
    // Present value at your death of the remaining survivor payments (joint amount continues)
    for (let m = 0; m < monthsSpouseSurvives; m++) {
      survivorPV += jointAmt * Math.pow(1 + colaMon, monthsYou + m) / Math.pow(1 + rMon, m + 1);
    }
    const singleBetter = fvDiff >= survivorPV;
    // Lump sum needed at retirement age to replace the joint (survivor) benefit for spouse
    let lumpSumNeeded = 0;
    for (let m = 0; m < (spouseLife - spouseAgeAtRet) * 12; m++) {
      lumpSumNeeded += jointAmt * Math.pow(1 + colaMon, m) / Math.pow(1 + rMon, m + 1);
    }
    return { fvDiff, survivorPV, singleBetter, lumpSumNeeded, diff, yearsYou, yearsSpouseSurvives };
  }, [retireAge, yourLife, spouseAgeAtRet, spouseLife, singleAmt, jointAmt, returnRate, cola]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Single-life or joint-and-survivor pension payout?</h3>
      <p className="text-sm text-slate-600 mb-4">A <b>single-life pension</b> means the employer will pay their employee's pension until their death. This payment option offers a higher payment per month but will not continue paying benefits to a spouse who outlives the retiree. In contrast, a <b>joint-and-survivor pension payout</b> pays a lower amount per month, but when the retiree dies, the surviving spouse will continue receiving benefits for the remainder of their life.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your retirement age</label><input type="number" value={retireAge} onChange={e=>setRetireAge(+e.target.value||0)} data-testid="pension-sj-retage"/></div>
          <div className="field"><label>Your life expectancy</label><input type="number" value={yourLife} onChange={e=>setYourLife(+e.target.value||0)}/></div>
          <div className="field"><label>Spouse's age when you retire</label><input type="number" value={spouseAgeAtRet} onChange={e=>setSpouseAgeAtRet(+e.target.value||0)}/></div>
          <div className="field"><label>Spouse's life expectancy</label><input type="number" value={spouseLife} onChange={e=>setSpouseLife(+e.target.value||0)}/></div>
          <div className="field"><label>Single life pension</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={singleAmt} onChange={e=>setSingleAmt(+e.target.value||0)} className="flex-1" data-testid="pension-sj-single"/><span className="self-center text-slate-500">per month</span></div></div>
          <div className="field"><label>Joint survivor pension</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={jointAmt} onChange={e=>setJointAmt(+e.target.value||0)} className="flex-1" data-testid="pension-sj-joint"/><span className="self-center text-slate-500">per month</span></div></div>
          <div className="field"><label>Your investment return</label><div className="flex gap-2"><input type="number" step="0.01" value={returnRate} onChange={e=>setReturnRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
          <div className="field"><label>Cost-of-living adjustment</label><div className="flex gap-2"><input type="number" step="0.01" value={cola} onChange={e=>setCola(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
        </div>
        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body">
            <p className="mb-3">Dying at age {yourLife} requires a lump sum of <b data-testid="pension-sj-lumpsum">{fmt(c.lumpSumNeeded)}</b> to replace your survivor pension benefit.</p>
            <p className="mb-3">From term life insurance perspective, if you can find a term life insurance with a monthly premium of <b>{fmt(c.lumpSumNeeded/240)}</b> or lower for {fmt(c.lumpSumNeeded)} and {c.yearsYou} years, it is better to take the single life pension payout option and purchase the term life insurance. This combination will provide the same or better coverage than the joint and survivor pension payout option.</p>
            <p className="mb-3">From investment perspective, <b className={c.singleBetter ? 'text-green-700' : 'text-red-700'}>it is better to take the {c.singleBetter ? 'single life' : 'joint and survivor'} pension payout option</b>. If you take the single life pension payout option, you can invest the payment difference of ${singleAmt} - ${jointAmt} = ${c.diff}. At the end of age {yourLife} (your life expectancy, your spouse age {spouseAgeAtRet + c.yearsYou}), the investment accumulation of the payment difference would reach <b>{fmt(c.fvDiff)}</b>, yet the value of the remaining survivor pension payout until your spouse reach {spouseLife} is <b>{fmt(c.survivorPV)}</b>.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
